/**
 * Bar show time slots — v2 JSON + legacy v1 migration.
 * Shared by server routes and client (webpack bundles this file).
 */

const DAY_ORDER = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];

/** getDay(): 0 Sun .. 6 Sat → mon..sun key */
function weekdayKeyFromDate(d) {
  const map = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];
  return map[d.getDay()];
}

function parseYmdLocal(ymd) {
  if (!ymd || typeof ymd !== "string") return null;
  const m = ymd.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return null;
  const y = Number(m[1]);
  const mo = Number(m[2]) - 1;
  const day = Number(m[3]);
  const d = new Date(y, mo, day, 12, 0, 0, 0);
  return Number.isNaN(d.getTime()) ? null : d;
}

function ensureSeconds(t) {
  if (t == null || t === "") return "";
  const s = String(t).trim();
  return s.length === 5 ? `${s}:00` : s;
}

function timeToMinutes(timeStr) {
  if (!timeStr) return null;
  const sub = String(timeStr).substring(0, 5);
  const parts = sub.split(":");
  if (parts.length < 2) return null;
  const h = Number(parts[0]);
  const min = Number(parts[1]);
  if (Number.isNaN(h) || Number.isNaN(min)) return null;
  return h * 60 + min;
}

/**
 * Whether show start_time falls in [slot.start, slot.end) with overnight support.
 */
function isStartInSlotRange(showStartTime, slot) {
  if (!showStartTime || !slot?.start_time || !slot?.end_time) return false;
  const showMinutes = timeToMinutes(showStartTime);
  const slotStartMinutes = timeToMinutes(slot.start_time);
  const slotEndMinutes = timeToMinutes(slot.end_time);
  if (
    showMinutes === null ||
    slotStartMinutes === null ||
    slotEndMinutes === null
  ) {
    return false;
  }
  if (slotEndMinutes < slotStartMinutes) {
    return (
      showMinutes >= slotStartMinutes || showMinutes <= slotEndMinutes
    );
  }
  return showMinutes >= slotStartMinutes && showMinutes < slotEndMinutes;
}

const WEEKDAY_LABEL_SHORT = {
  mon: "Mon",
  tue: "Tue",
  wed: "Wed",
  thu: "Thu",
  fri: "Fri",
  sat: "Sat",
  sun: "Sun",
};

/**
 * Parse DB/API value to normalized v2 object (does not renumber ids).
 */
function parseTimeSlots(raw) {
  let obj = raw;
  if (raw == null) {
    return {
      version: 2,
      enabled: false,
      defaultSlots: [],
      byDay: {},
    };
  }
  if (typeof raw === "string") {
    try {
      obj = JSON.parse(raw);
    } catch {
      return {
        version: 2,
        enabled: false,
        defaultSlots: [],
        byDay: {},
      };
    }
  }
  if (!obj || typeof obj !== "object") {
    return {
      version: 2,
      enabled: false,
      defaultSlots: [],
      byDay: {},
    };
  }

  // Legacy v1: { slots: [...] }
  if (!obj.version && Array.isArray(obj.slots)) {
    return {
      version: 2,
      enabled: true,
      defaultSlots: obj.slots.map((s) => ({ ...s })),
      byDay: {},
    };
  }

  const enabled = obj.enabled !== false;
  const defaultSlots = Array.isArray(obj.defaultSlots)
    ? obj.defaultSlots.map((s) => ({ ...s }))
    : [];

  const byDay = {};
  if (obj.byDay && typeof obj.byDay === "object") {
    for (const k of DAY_ORDER) {
      if (Object.prototype.hasOwnProperty.call(obj.byDay, k)) {
        const v = obj.byDay[k];
        if (v === null || v === undefined) {
          byDay[k] = null;
        } else if (Array.isArray(v)) {
          byDay[k] = v.map((s) => ({ ...s }));
        }
      }
    }
  }

  return {
    version: 2,
    enabled,
    defaultSlots,
    byDay,
  };
}

function getEffectiveSlotsForWeekday(parsed, weekdayKey) {
  const p = parseTimeSlots(parsed);
  if (!p.enabled) return [];
  const wd = weekdayKey && DAY_ORDER.includes(weekdayKey) ? weekdayKey : "mon";
  if (Object.prototype.hasOwnProperty.call(p.byDay, wd)) {
    const override = p.byDay[wd];
    if (override === null || override === undefined) {
      return Array.isArray(p.defaultSlots) ? [...p.defaultSlots] : [];
    }
    return Array.isArray(override) ? [...override] : [];
  }
  return Array.isArray(p.defaultSlots) ? [...p.defaultSlots] : [];
}

/**
 * @param {object|string|null} parsedOrRaw — bar.time_slots
 * @param {string} ymd — YYYY-MM-DD
 * @returns {Array<{id,name,start_time,end_time}>}
 */
function getEffectiveSlotsForDate(parsedOrRaw, ymd) {
  const d = parseYmdLocal(ymd);
  if (!d) return [];
  const wd = weekdayKeyFromDate(d);
  return getEffectiveSlotsForWeekday(parsedOrRaw, wd);
}

/**
 * First matching slot for show.start_time, or null.
 */
function matchShowToSlot(show, slots) {
  if (!show?.start_time || !Array.isArray(slots)) return null;
  for (let i = 0; i < slots.length; i++) {
    if (isStartInSlotRange(show.start_time, slots[i])) {
      return slots[i];
    }
  }
  return null;
}

const UNASSIGNED_KEY = "__unassigned__";
const UNASSIGNED_LABEL = "Unassigned";

function sortFinancialSlotLabels(labels) {
  const u = UNASSIGNED_LABEL;
  return [...(labels || [])].sort((a, b) => {
    if (a === u) return 1;
    if (b === u) return -1;
    return String(a).localeCompare(String(b));
  });
}

/**
 * @returns {{ bucketKey: string, label: string, slot: object|null }}
 */
function ymdFromShowDate(dateVal) {
  if (!dateVal) return "";
  if (typeof dateVal === "string") return dateVal.substring(0, 10);
  if (dateVal instanceof Date) return formatYmd(dateVal);
  const s = String(dateVal);
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.substring(0, 10);
  return "";
}

function assignShowToBucket(show, parsedOrRaw) {
  const p = parseTimeSlots(parsedOrRaw);
  const ymd = ymdFromShowDate(show?.date);
  const d = parseYmdLocal(ymd);
  if (!d) {
    return { bucketKey: UNASSIGNED_KEY, label: UNASSIGNED_LABEL, slot: null };
  }
  const wd = weekdayKeyFromDate(d);
  if (!p.enabled) {
    return { bucketKey: UNASSIGNED_KEY, label: UNASSIGNED_LABEL, slot: null };
  }
  const slots = getEffectiveSlotsForWeekday(p, wd);
  if (slots.length === 0) {
    return { bucketKey: UNASSIGNED_KEY, label: UNASSIGNED_LABEL, slot: null };
  }
  const slot = matchShowToSlot(show, slots);
  if (!slot) {
    return { bucketKey: UNASSIGNED_KEY, label: UNASSIGNED_LABEL, slot: null };
  }
  const short = WEEKDAY_LABEL_SHORT[wd] || wd;
  const label = `${slot.name} · ${short}`;
  const bucketKey = `${wd}|${slot.id}`;
  return { bucketKey, label, slot };
}

function formatYmd(d) {
  const x = new Date(d);
  if (Number.isNaN(x.getTime())) return "";
  const y = x.getFullYear();
  const m = String(x.getMonth() + 1).padStart(2, "0");
  const day = String(x.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function validateSlotShape(slot, idx) {
  const prefix = `Slot ${idx + 1}`;
  if (!slot || typeof slot !== "object") {
    return `${prefix}: invalid`;
  }
  if (!slot.name || !String(slot.name).trim()) {
    return `${prefix}: name is required`;
  }
  const st = ensureSeconds(slot.start_time);
  const en = ensureSeconds(slot.end_time);
  if (!st || !en) {
    return `${prefix}: start and end time are required`;
  }
  return null;
}

/**
 * Validate and normalize v2 payload for API (renumber ids uniquely).
 * @returns {{ ok: boolean, error?: string, normalized?: object }}
 */
function validateAndNormalizeTimeSlotsPayload(input) {
  if (input == null || input === "") {
    return {
      ok: true,
      normalized: { version: 2, enabled: false, defaultSlots: [], byDay: {} },
    };
  }

  let body = input;
  if (typeof input === "string") {
    try {
      body = JSON.parse(input);
    } catch {
      return { ok: false, error: "Invalid time_slots JSON" };
    }
  }

  if (typeof body !== "object" || body === null) {
    return { ok: false, error: "Invalid time_slots" };
  }

  // Accept legacy v1 on write by normalizing
  const parsed = parseTimeSlots(body);
  const enabled = body.enabled !== false && parsed.enabled !== false;

  if (!enabled) {
    return {
      ok: true,
      normalized: { version: 2, enabled: false, defaultSlots: [], byDay: {} },
    };
  }

  const defaultSlots = Array.isArray(body.defaultSlots)
    ? body.defaultSlots
    : !body.version && Array.isArray(body.slots)
      ? body.slots
      : parsed.defaultSlots;

  const byDayIn = body.byDay && typeof body.byDay === "object" ? body.byDay : {};
  const byDay = {};

  const validateList = (arr, ctx) => {
    if (!Array.isArray(arr)) {
      return `${ctx} must be an array`;
    }
    if (arr.length > 6) {
      return `${ctx} cannot have more than 6 slots`;
    }
    for (let i = 0; i < arr.length; i++) {
      const err = validateSlotShape(arr[i], i);
      if (err) return `${ctx}: ${err}`;
    }
    return null;
  };

  const defErr = validateList(defaultSlots, "defaultSlots");
  if (defErr) return { ok: false, error: defErr };

  for (const k of DAY_ORDER) {
    if (!Object.prototype.hasOwnProperty.call(byDayIn, k)) continue;
    const v = byDayIn[k];
    if (v === null || v === undefined) {
      byDay[k] = null;
      continue;
    }
    if (!Array.isArray(v)) {
      return { ok: false, error: `byDay.${k} must be an array or null` };
    }
    const err = validateList(v, `byDay.${k}`);
    if (err) return { ok: false, error: err };
    byDay[k] = v.map((s) => ({ ...s }));
  }

  const normalized = renumberIds({
    version: 2,
    enabled: true,
    defaultSlots: defaultSlots.map((s) => ({ ...s })),
    byDay,
  });

  return { ok: true, normalized };
}

function renumberIds(v2) {
  let n = 1;
  const nextId = () => n++;

  const cloneSlot = (s) => ({
    id: nextId(),
    name: String(s.name).trim(),
    start_time: ensureSeconds(s.start_time),
    end_time: ensureSeconds(s.end_time),
  });

  const defaultSlots = (v2.defaultSlots || []).map(cloneSlot);
  const byDay = {};
  for (const k of DAY_ORDER) {
    if (!Object.prototype.hasOwnProperty.call(v2.byDay, k)) continue;
    const v = v2.byDay[k];
    if (v === null || v === undefined) {
      byDay[k] = null;
    } else if (Array.isArray(v)) {
      byDay[k] = v.map(cloneSlot);
    }
  }
  return {
    version: 2,
    enabled: v2.enabled !== false,
    defaultSlots,
    byDay,
  };
}

function serializeTimeSlotsForDb(obj) {
  return JSON.stringify(obj);
}

module.exports = {
  DAY_ORDER,
  WEEKDAY_LABEL_SHORT,
  UNASSIGNED_KEY,
  UNASSIGNED_LABEL,
  sortFinancialSlotLabels,
  ymdFromShowDate,
  parseTimeSlots,
  parseYmdLocal,
  weekdayKeyFromDate,
  getEffectiveSlotsForWeekday,
  getEffectiveSlotsForDate,
  isStartInSlotRange,
  matchShowToSlot,
  assignShowToBucket,
  validateAndNormalizeTimeSlotsPayload,
  renumberIds,
  ensureSeconds,
  serializeTimeSlotsForDb,
  timeToMinutes,
};
